import type { Dispatch, SetStateAction } from 'react';
import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '@eti/authentication';
import { useTranslation, useProperty } from '@eti/providers';
import { AuthenticationType, LoginSource } from '@eti/schema-types';
import { scrollToElement } from '../../../utils/scrollToElement';
import { useScript } from '../custom-hooks/scripting';
import { CRITICAL } from '../utils/messageTypes';
import { routes } from '../../../constants/routesAndApisConstants';

interface GoogleProps {
  callback?: () => void;
  setNotification: Dispatch<SetStateAction<{ message: string; type: string; dataTestId: string }>>;
}

const Google = ({ callback, setNotification }: GoogleProps) => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const history = useHistory();
  const { authenticateUser } = useAuth();
  const clientID = p('IbeClient.MyBookings.Google.Client.Id');

  const authenticate = useCallback(
    async (token) => {
      const authRequest = {
        email: null,
        orderNumber: null,
        authenticationType: AuthenticationType.Google,
        token,
        loginSource: LoginSource.OrderLogin,
      };
      const { data } = await authenticateUser(authRequest);
      const authenticateUserData = data?.authenticateUser;

      return authenticateUserData?.authenticated;
    },
    [authenticateUser],
  );

  const handleResponse = useCallback(
    async (response) => {
      if (!response.credential) {
        return;
      }

      try {
        const authenticated = await authenticate(response.credential);
        if (authenticated) {
          if (callback) {
            callback();
          } else {
            history.push(routes.ORDER_LIST);
          }
        } else {
          setNotification({
            message: t('Postbooking.Login.Internal.Error'),
            type: CRITICAL,
            dataTestId: 'login-service-error-message',
          });

          scrollToElement('#loginMessage');
        }
      } catch {
        setNotification({
          message: t('Postbooking.Login.Internal.Error'),
          type: CRITICAL,
          dataTestId: 'login-service-error-message',
        });

        scrollToElement('#loginMessage');
      }
    },
    [authenticate, callback, history, setNotification, t],
  );

  const onLoad = useCallback(() => {
    // eslint-disable-next-line no-undef
    google.accounts.id.initialize({
      client_id: `${clientID}.apps.googleusercontent.com`,
      callback: handleResponse,
    });

    const googleButton = document.getElementById('google-signin-btn');

    if (googleButton) {
      // eslint-disable-next-line no-undef
      google.accounts.id.renderButton(googleButton, {
        type: 'standard',
        size: 'large',
        text: 'signin_with',
        theme: 'outline',
        width: 296,
      });
    }
  }, [clientID, handleResponse]);

  useScript('google-signin', 'https://accounts.google.com/gsi/client', onLoad);

  return <div data-testid="google-button" id="google-signin-btn" />;
};

export default Google;
